import { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useAuth } from '@/hooks/useAuth';
import { useWorkoutRoutines } from '@/hooks/useWorkoutRoutines';

function parseDate(display: string): string | undefined {
  if (!display.trim()) return undefined;
  const [d, m, y] = display.split('/');
  if (!d || !m || !y || y.length !== 4) return undefined;
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

/**
 * Máscara de data DD/MM/AAAA: insere as barras automaticamente conforme
 * o usuário digita. Mantém só dígitos (até 8) e formata 06062026 → 06/06/2026.
 * Backspace funciona naturalmente — a barra some quando o dígito antes dela é apagado.
 */
function maskDate(input: string): string {
  const digits = input.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

export default function NewRoutineScreen() {
  const { studentId } = useLocalSearchParams<{ studentId: string }>();
  const { profile } = useAuth();
  const { createRoutine } = useWorkoutRoutines(studentId);
  const router = useRouter();
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleCreate() {
    if (!name.trim()) { setError('Dê um nome para a rotina'); return; }
    setError('');
    setLoading(true);
    try {
      const routine = await createRoutine({
        name: name.trim(),
        personal_id: profile!.id,
        start_date: parseDate(startDate),
        end_date: parseDate(endDate),
      });
      router.replace(`/(personal)/routines/${routine.id}` as any);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao criar rotina');
    } finally {
      setLoading(false);
    }
  }

  return (
    <View style={styles.screen}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()}>
          <Text style={styles.backBtn}>← Voltar</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Nova Rotina</Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView contentContainerStyle={styles.form} keyboardShouldPersistTaps="handled">
        <Text style={styles.label}>Nome da rotina *</Text>
        <TextInput
          style={styles.input}
          value={name}
          onChangeText={setName}
          placeholder="Ex: Hipertrofia Inicial"
          returnKeyType="next"
        />

        <Text style={[styles.label, { marginTop: 20 }]}>Data de início</Text>
        <TextInput
          style={styles.input}
          value={startDate}
          onChangeText={(t) => setStartDate(maskDate(t))}
          placeholder="DD/MM/AAAA"
          keyboardType="numeric"
          maxLength={10}
        />

        <Text style={[styles.label, { marginTop: 20 }]}>Data de fim</Text>
        <TextInput
          style={styles.input}
          value={endDate}
          onChangeText={(t) => setEndDate(maskDate(t))}
          placeholder="DD/MM/AAAA"
          keyboardType="numeric"
          maxLength={10}
        />

        {error ? <Text style={styles.error}>{error}</Text> : null}

        <TouchableOpacity style={styles.button} onPress={handleCreate} disabled={loading}>
          <Text style={styles.buttonText}>{loading ? 'Criando...' : 'Criar Rotina'}</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#F5F5F5' },
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 14,
    backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#eee',
  },
  backBtn: { color: '#C8102E', fontSize: 15, fontWeight: '600' },
  headerTitle: { fontSize: 17, fontWeight: '700', color: '#1A1A1A' },
  headerSpacer: { width: 60 },
  form: { padding: 20 },
  label: { fontSize: 13, fontWeight: '600', color: '#555', marginBottom: 8 },
  input: {
    backgroundColor: '#fff', borderWidth: 1, borderColor: '#ddd',
    borderRadius: 10, padding: 14, fontSize: 15, color: '#1A1A1A',
  },
  error: { color: '#C8102E', fontSize: 13, marginTop: 16 },
  button: {
    backgroundColor: '#C8102E', padding: 16, borderRadius: 10,
    alignItems: 'center', marginTop: 28,
  },
  buttonText: { color: '#fff', fontWeight: 'bold', fontSize: 16 },
});
